// Modular-pricing entitlements — the single decision function for what a
// modular account can access, plus the API-prefix map the global server
// middleware (server/lib/entitlements.ts) enforces it with.
//
// WHY THIS FILE EXISTS
// --------------------
// The modular model (shared/modular-pricing.ts) prices access per feature:
// a base tier (Essentials core / Pro all-inclusive), à-la-carte per-room
// modules, and kind-specific add-ons. This module is the ONE place that
// tier + modules + add-ons + trial + billing status resolve into a concrete
// set of unlocked feature keys, consumed by:
//   - `enforceEntitlements` (server/lib/entitlements.ts) — the global API
//     choke point that replaced `enforceMenuTierLocks` for modular accounts,
//   - `requireEntitlement(key)` for route-level gates,
//   - the client entitlement hook / upgrade prompts.
//
// Mirrors the architecture of ./feature-gates.ts and ./menu-tier-locks.ts
// (boundary-aware prefix match, registry-driven, one pure decision helper).

import { pathMatchesPrefix } from "./feature-gates";
import { resolveV3State, v3StateUnlocksEverything } from "./billing-entitlement";
import {
  DEVICE_MODULE_KEYS,
  ESSENTIALS_CORE_FEATURES,
  OPERATOR_MODULE_KEYS,
  type AccountKind,
  type ModuleKey,
  type PlanTier,
} from "./modular-pricing";

export { pathMatchesPrefix };

/**
 * Every gateable feature key on the modular model.
 *   - Essentials core: included in both tiers, on for any billing-active account.
 *   - Modules: à-la-carte on Essentials, all included in Pro.
 *   - Add-ons: operator-only (revenue/research) and PM-only (owner_portals/
 *     cpm_listing) purchases.
 *   - `pnl_base`: the free PadSplit-revenue P&L — on for EVERY non-archived
 *     account, even lapsed ones (the spec's one always-free carve-out).
 */
export const ENTITLEMENT_KEYS = [
  ...ESSENTIALS_CORE_FEATURES,
  ...OPERATOR_MODULE_KEYS,
  ...DEVICE_MODULE_KEYS,
  "revenue",
  "research",
  "owner_portals",
  "cpm_listing",
  "pnl_base",
] as const;
export type EntitlementKey = (typeof ENTITLEMENT_KEYS)[number];

export function isEntitlementKey(key: string): key is EntitlementKey {
  return (ENTITLEMENT_KEYS as readonly string[]).includes(key);
}

export interface EntitlementInput {
  /** NULL = legacy account not yet migrated — computeEntitlements never runs for those. */
  accountKind: AccountKind | null;
  planTier: PlanTier | null;
  /** app_users.status ('active' | 'waitlist' | 'suspended' | 'invited' | 'archived'). */
  status: string | null;
  subscriptionStatus: string | null;
  trialEndsAt: Date | string | null;
  archivedAt: Date | string | null;
  /** Keys of account_modules rows with status='active'. Unknown keys are ignored. */
  activeModules: readonly string[];
  revenueAddonActive: boolean;
  researchAddonActive: boolean;
  cpmListingActive: boolean;
  /**
   * 'v3' = per-property pricing (shared/pricing.ts): every kept feature is
   * included for trialing/active/lifetime/comped (and readable while paused or
   * past due); the free Research plan unlocks research only. Module, tier and
   * room gating do not apply. NULL/undefined = the modular rules below.
   */
  billingModel?: string | null;
  pausedAt?: Date | string | null;
  pastDueSince?: Date | string | null;
  lifetimeAllowance?: number | null;
  /** Injectable for tests; defaults to the real clock. */
  now?: Date;
}

function toDate(value: Date | string | null): Date | null {
  if (value == null) return null;
  return value instanceof Date ? value : new Date(value);
}

/** True while the account's 14-day full-access trial is still running. */
export function isInModularTrial(input: Pick<EntitlementInput, "trialEndsAt" | "now">): boolean {
  const ends = toDate(input.trialEndsAt);
  if (!ends || Number.isNaN(ends.getTime())) return false;
  return ends.getTime() > (input.now ?? new Date()).getTime();
}

/**
 * Whether the account's own billing state grants access: a live/comped Stripe
 * status, or a still-running trial. (Team-member inheritance of an inviter's
 * billing is layered on in server/lib/entitlements.ts, not here.)
 */
export function isBillingActive(
  input: Pick<EntitlementInput, "subscriptionStatus" | "trialEndsAt" | "now"> &
    Partial<Pick<EntitlementInput, "billingModel" | "pausedAt" | "pastDueSince" | "lifetimeAllowance">>,
): boolean {
  if (input.billingModel === "v3") return v3StateUnlocksEverything(v3StateFor(input).state);
  if (["active", "trialing", "comped"].includes(input.subscriptionStatus ?? "")) return true;
  return isInModularTrial(input);
}

const ALL_MODULE_KEYS: readonly ModuleKey[] = [...OPERATOR_MODULE_KEYS, ...DEVICE_MODULE_KEYS];

function v3StateFor(
  input: Pick<EntitlementInput, "subscriptionStatus" | "trialEndsAt" | "now"> &
    Partial<Pick<EntitlementInput, "pausedAt" | "pastDueSince" | "lifetimeAllowance">>,
) {
  return resolveV3State({
    subscriptionStatus: input.subscriptionStatus,
    trialEndsAt: input.trialEndsAt,
    pausedAt: input.pausedAt ?? null,
    pastDueSince: input.pastDueSince ?? null,
    lifetimeAllowance: input.lifetimeAllowance ?? null,
    now: input.now,
  });
}

/**
 * Resolve a modular account's unlocked feature keys. Pure — the ONE place
 * plan/module/add-on/trial/billing state becomes access:
 *
 *   archived            → {} (offboarded; data retained, access off)
 *   waitlist            → {} (blocked upstream with WAITLIST_ONLY)
 *   lapsed / unpaid     → { pnl_base } only
 *   in 14-day trial     → EVERYTHING appropriate to the account kind
 *   billing-active      → core ∪ (Pro ? all modules : purchased modules)
 *                          ∪ purchased kind-appropriate add-ons ∪ pnl_base
 *
 * Kind rules regardless of purchases: research never appears for property
 * managers; owner_portals/cpm_listing never for operators. Revenue (the
 * income/expense/Mercury suite) is a purchasable add-on for operators and is
 * INCLUDED in PM Pro — a property manager holds it on Pro, while comped, or
 * during the trial, never through the operator add-on toggle (PM Essentials
 * has no purchase path to it).
 * owner_portals is usage-billed (per owner), so a billing-active PM always
 * holds it; cpm_listing requires its flat-fee toggle outside the trial.
 * Comped accounts hold ALL kind-appropriate add-ons automatically (like the
 * trial branch) — comped Pro operators get every module AND every add-on.
 */
export function computeEntitlements(input: EntitlementInput): ReadonlySet<EntitlementKey> {
  const set = new Set<EntitlementKey>();
  if (input.status === "archived" || toDate(input.archivedAt) != null) return set;
  if (input.status === "waitlist") return set;

  // The free PadSplit-revenue P&L survives even a lapsed subscription.
  set.add("pnl_base");

  if (input.billingModel === "v3") {
    if (v3StateUnlocksEverything(v3StateFor(input).state)) {
      for (const key of ENTITLEMENT_KEYS) set.add(key);
    } else {
      set.add("research");
    }
    return set;
  }

  if (!isBillingActive(input)) return set;

  for (const key of ESSENTIALS_CORE_FEATURES) set.add(key);

  const operator = input.accountKind === "operator";
  const trial = isInModularTrial(input);
  // Comped accounts (founders/staff) hold every kind-appropriate add-on
  // automatically — same as the trial branch — without any purchase flags.
  const comped = input.subscriptionStatus === "comped";

  if (trial || input.planTier === "pro") {
    for (const key of ALL_MODULE_KEYS) set.add(key);
  } else {
    for (const key of input.activeModules) {
      if ((ALL_MODULE_KEYS as readonly string[]).includes(key)) set.add(key as EntitlementKey);
    }
  }

  if (operator) {
    if (trial || comped || input.revenueAddonActive) set.add("revenue");
    if (trial || comped || input.researchAddonActive) set.add("research");
  } else {
    set.add("owner_portals");
    if (trial || comped || input.cpmListingActive) set.add("cpm_listing");
    // PM Pro includes the Revenue suite (Income, Expenses, Mercury). Without
    // this a PM Pro account is locked out of its own ledger data the moment
    // the trial ends, with no purchase path to get it back.
    if (trial || comped || input.planTier === "pro") set.add("revenue");
  }

  return set;
}

/**
 * Entitlement key → the `/api/app/*` path prefixes it guards (boundary-prefix
 * match, seeded from MENU_TIER_LOCK_API_PREFIXES — the best path→feature map
 * in the codebase). A path may appear under SEVERAL keys (e.g. the devices
 * overview): the request is allowed when the account holds ANY matching key.
 *
 * Prefixes intentionally absent (not enforced here):
 *   - vehicles / todos-in-members UI etc. with no dedicated surface beyond the
 *     ones listed (UI-only, rendered from already-guarded data),
 *   - AI message enhancer — parameterized sub-path of /members with its own
 *     dedicated beta-aware enforcer (requireAiDraftingAccess),
 *   - cpm_listing — no API surface yet,
 *   - infrastructure (acquisitions, buy-box, underwriting, vendors, image
 *     generator, …): open to any billing-active account on the modular model.
 */
export const ENTITLEMENT_API_PREFIXES: Readonly<Partial<Record<EntitlementKey, readonly string[]>>> = {
  properties: ["/api/app/properties", "/api/app/property-groups", "/api/app/dashboard", "/api/app/dashboard-preferences"],
  members: ["/api/app/members"],
  booking_requests: ["/api/app/booking-requests"],
  messages: ["/api/app/messages", "/api/app/messages-pending"],
  tasks: ["/api/app/tasks", "/api/app/operations/tasks", "/api/app/operations/task-rules", "/api/app/service-forms"],
  todos: ["/api/app/todos"],
  supplies: [
    "/api/app/supply-items",
    "/api/app/supply-orders",
    "/api/app/supply-recurring-schedules",
    "/api/app/supply-property-settings",
    "/api/app/supply-categories",
  ],
  collections: ["/api/app/collections"],
  cleaning: ["/api/app/cleaning"],
  dynamic_pricing: ["/api/app/pricing-models", "/api/app/pricing-suggestions"],
  marketing: ["/api/app/marketing"],
  analytics: ["/api/app/analytics"],
  locks: [
    "/api/app/devices/seam/locks",
    "/api/app/devices/seam/access-codes",
    "/api/app/devices/locks",
    "/api/app/devices/lock-member-assignments",
    "/api/app/devices/lock-health-mutes",
    "/api/app/devices/padsplit-locks",
    "/api/app/padsplit/harvest/locks",
    "/api/app/devices/overview",
    "/api/app/devices/connections",
  ],
  thermostats: [
    "/api/app/devices/seam/thermostats",
    "/api/app/devices/thermostat-presets",
    "/api/app/devices/thermostat-settings",
    "/api/app/devices/overview",
    "/api/app/devices/connections",
  ],
  revenue: [
    "/api/app/income",
    "/api/app/expenses",
    "/api/app/expense-category-settings",
    "/api/app/mercury-connection",
    "/api/app/mercury",
  ],
  research: ["/api/app/connections"],
  owner_portals: [
    "/api/app/operations/owners",
    "/api/app/operations/owner-invoices",
    "/api/app/account/pm-fee-settings",
  ],
  pnl_base: ["/api/app/financials/pnl", "/api/app/financials/padsplit-import"],
};

/**
 * Menu-tier-lock registry key → the modular entitlement(s) that unlock that
 * sidebar section (any-of). Drives the sidebar lock emblems + /denied
 * endpoint for MODULAR accounts, replacing legacy tier math. Keys absent
 * here are core/infrastructure — never locked for a billing-active account.
 */
export const MENU_LOCK_KEY_ENTITLEMENTS: Readonly<Record<string, readonly EntitlementKey[]>> = {
  "operations.supplies": ["supplies"],
  "operations.marketing": ["marketing"],
  "revenue.collections": ["collections"],
  "operations.devices_overview": ["locks", "thermostats"],
  "operations.devices_smart_locks": ["locks"],
  "operations.devices_thermostats": ["thermostats"],
  "revenue.pricing_rooms": ["dynamic_pricing"],
  "revenue.income": ["revenue"],
  "revenue.expenses": ["revenue"],
  "revenue.financials_settings": ["revenue"],
  "revenue.profit_loss": ["pnl_base"],
  "pinned.connections": ["research"],
  "owners.owners_directory": ["owner_portals"],
  "owners.owner_invoices": ["owner_portals"],
  "owners.owner_settings": ["owner_portals"],
};

export interface EntitlementApiMatch {
  key: EntitlementKey;
  prefix: string;
}

/** All entitlement keys whose API prefixes cover `path` (each carries the prefix). */
export function matchEntitlementApiTargets(path: string): EntitlementApiMatch[] {
  const out: EntitlementApiMatch[] = [];
  for (const key of Object.keys(ENTITLEMENT_API_PREFIXES) as EntitlementKey[]) {
    for (const prefix of ENTITLEMENT_API_PREFIXES[key] ?? []) {
      if (pathMatchesPrefix(path, prefix)) out.push({ key, prefix });
    }
  }
  return out;
}

export type EntitlementApiDecision = { blocked: boolean; key?: EntitlementKey };

/**
 * A matched request is allowed when the account holds ANY of the matched keys
 * (a path shared by several features — e.g. the devices overview — unlocks
 * with whichever module the account bought). When blocked, the surfaced key is
 * the longest-prefix match so upgrade prompts name the most specific feature.
 */
export function resolveEntitlementApiDecision(
  matches: readonly EntitlementApiMatch[],
  entitlements: ReadonlySet<EntitlementKey>,
): EntitlementApiDecision {
  if (matches.length === 0) return { blocked: false };
  let best: EntitlementApiMatch | null = null;
  for (const m of matches) {
    if (entitlements.has(m.key)) return { blocked: false };
    if (!best || m.prefix.length > best.prefix.length) best = m;
  }
  return { blocked: true, key: best!.key };
}
